import { useMemo, useState } from "react";
import { Box, Text, TextInput, ActionIcon } from "@mantine/core";
import {
  IconDownload,
  IconUpload,
  IconPlus,
  IconEdit,
  IconTrash,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { TableActions, TableAction, actionsColumn } from "../TableActions";
import { useSetActionButtons } from "../../services/ActionButtonsContext";
import { openExportModal } from "../Translations/ExportModal";
import { openImportModal } from "../Translations/ImportModal";
import { DataTable } from "../DataTable";
import { useGetAllTranslations } from "../../api/translations";
import { useSearchParams } from "react-router-dom";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import TranslationModal from "../Translations/TranslationModal";
import DeleteTranslationModal from "../Translations/DeleteTranslationModal";
import VersionInfoCard from "../Translations/VersionInfoCard";
import CoverageCard from "../Translations/CoverageCard";
import { useGetAllActiveLanguages } from "../../api/languages";

function TranslationsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);

  const [editingKey, setEditingKey] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const page = parseInt(searchParams.get("page") || 1);
  const limit = parseInt(searchParams.get("limit") || 25);

  const sorting = useMemo(() => {
    try {
      const sortParams = searchParams.get("sort");
      return sortParams
        ? JSON.parse(sortParams)
        : { columnAccessor: "translationKey", direction: "asc" };
    } catch (e) {
      return { columnAccessor: "translationKey", direction: "asc" };
    }
  }, [searchParams]);

  const filter = useMemo(() => {
    try {
      const filterParams = searchParams.get("filter");
      return filterParams ? JSON.parse(filterParams) : null;
    } catch (e) {
      return null;
    }
  }, [searchParams]);

  const [debouncedFilter] = useDebouncedValue(filter, 300);

  const { data, isPending } = useGetAllTranslations({
    page,
    limit,
    sort: JSON.stringify(sorting),
    filter: debouncedFilter,
  });

  const { data: languages } = useGetAllActiveLanguages();

  const LANGUAGES = languages?.data?.map((lang) => lang.code) || [];

  const actions = useMemo(
    () => [
      {
        label: "Add Translation",
        icon: <IconPlus />,
        onClick: () => {
          setEditingKey(null);
          openModal();
        },
      },
      {
        label: "Import",
        icon: <IconUpload />,
        onClick: openImportModal,
      },
      {
        label: "Export",
        icon: <IconDownload />,
        onClick: openExportModal,
      },
    ],
    [openModal]
  );

  useSetActionButtons(actions);

  const handlePageChange = (newPage) => {
    setSearchParams((params) => {
      params.set("page", newPage.toString());
      return params;
    });
  };

  const handleSortChange = (sort) => {
    setSearchParams((params) => {
      if (!sort) {
        params.delete("sort");
      } else {
        params.set("sort", JSON.stringify(sort));
      }
      return params;
    });
  };

  const handleFilterChange = (newFilter) => {
    setSearchParams((params) => {
      params.set("page", "1");
      if (!newFilter) {
        params.delete("filter");
      } else {
        params.set("filter", JSON.stringify(newFilter));
      }
      return params;
    });
  };

  const handleEdit = (translationKey) => {
    setEditingKey(translationKey);
    openModal();
  };

  const handleCloseModal = () => {
    setEditingKey(null);
    closeModal();
  };

  const handleDelete = (record) => {
    setRecordToDelete(record);
    openDeleteModal();
  };

  const handleCloseDeleteModal = () => {
    setRecordToDelete(null);
    closeDeleteModal();
  };

  const sortedData = useMemo(() => {
    if (!data?.data) return [];

    const records = [...data.data];

    if (sorting?.columnAccessor?.startsWith("languages.")) {
      const langCode = sorting.columnAccessor.replace("languages.", "");

      records.sort((a, b) => {
        const valA = a.languages[langCode] || "";
        const valB = b.languages[langCode] || "";
        const comparison = valA.localeCompare(valB);
        return sorting.direction === "asc" ? comparison : -comparison;
      });
    }

    return records;
  }, [data?.data, sorting]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessor: "translationKey",
        title: "Translation Key",
        sortable: true,
        render: ({ translationKey }) => (
          <Text size="sm" fw={500}>
            {translationKey}
          </Text>
        ),
        filter: (
          <TextInput
            label="Translation Key"
            description="Filter by translation key"
            placeholder="Search..."
            leftSection={<IconSearch size={16} />}
            rightSection={
              filter?.translationKey && (
                <ActionIcon
                  size="sm"
                  variant="transparent"
                  c="dimmed"
                  onClick={() => handleFilterChange(null)}
                >
                  <IconX size={14} />
                </ActionIcon>
              )
            }
            value={filter?.translationKey || ""}
            onChange={(e) => {
              const value = e.currentTarget.value;
              handleFilterChange(value ? { translationKey: value } : null);
            }}
          />
        ),
        filtering: filter?.translationKey,
      },
    ];

    const languageColumns = LANGUAGES?.map((langCode) => ({
      accessor: `languages.${langCode}`,
      title: langCode.toUpperCase(),
      sortable: true,
      render: (record) => (
        <Text size="sm" lineClamp={2}>
          {record.languages[langCode] || "-"}
        </Text>
      ),
    }));

    const actionColumn = actionsColumn(
      (record) => (
        <TableActions>
          <TableAction
            label="Edit translation"
            icon={IconEdit}
            color="blue"
            onClick={() => handleEdit(record.translationKey)}
          />
          <TableAction
            label="Delete translation key"
            icon={IconTrash}
            color="red"
            onClick={() => handleDelete(record)}
          />
        </TableActions>
      ),
      { title: "Actions" }
    );

    return [...baseColumns, ...languageColumns, actionColumn];
  }, [filter, LANGUAGES]);

  return (
    <Box>
      <VersionInfoCard />
      <CoverageCard />

      <DataTable
        records={sortedData}
        idAccessor="translationKey"
        pagination={{ page, limit }}
        onPageChange={handlePageChange}
        page={page}
        recordsPerPage={limit}
        totalRecords={data?.pagination?.total}
        onSortStatusChange={handleSortChange}
        sortStatus={sorting}
        borderRadius="md"
        verticalSpacing="md"
        columns={columns}
        fetching={isPending}
      />

      <TranslationModal
        opened={modalOpened}
        onClose={handleCloseModal}
        translationKey={editingKey}
        mode={editingKey ? "edit" : "create"}
        validLanguages={LANGUAGES}
      />

      <DeleteTranslationModal
        opened={deleteModalOpened}
        onClose={handleCloseDeleteModal}
        record={recordToDelete}
        validLanguages={LANGUAGES}
      />
    </Box>
  );
}

export default TranslationsTab;
